import type { SerializedNode, ConversionContext } from '../types';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseBoolean,
  parseInteger,
  parseFloat_,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
  type PositionValue,
} from '../utils';
import { COUNTDOWN_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import {
  COUNTDOWN_NODE_NAME,
  EXTERNAL_LINK_EVENT,
  NO_LINK_EVENT,
  STAY_ON_PAGE,
} from '../../utils/constants';

/**
 * Transform a <countdown> XML element into a CraftJS Countdown node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Countdown
 */
export const transformCountdown = (
  element: Element,
  context: ConversionContext,
): SerializedNode => {
  // Parse mode
  const modeAttr = getAttribute(element, 'mode');
  const mode = mapMode(modeAttr) || COUNTDOWN_DEFAULTS.mode;

  // Parse dateTime (default to 24h from now if not specified)
  const dateTimeAttr = getAttribute(element, 'dateTime');
  const dateTime = dateTimeAttr || getDefaultDateTime(mode);

  // Parse evergreen settings
  const evergreenIdAttr = getAttribute(element, 'evergreenId');
  const evergreenId = evergreenIdAttr || generateEvergreenId();

  const evergreenDurationAttr = getAttribute(element, 'evergreenDuration');
  const evergreenDuration = parseInteger(
    evergreenDurationAttr,
    COUNTDOWN_DEFAULTS.evergreenDuration,
  );

  // Parse type (display format)
  const typeAttr = getAttribute(element, 'type');
  const type = mapType(typeAttr) || COUNTDOWN_DEFAULTS.type;

  // Parse unit visibility
  const showDaysAttr = getAttribute(element, 'showDays');
  const showDays = parseBoolean(showDaysAttr, COUNTDOWN_DEFAULTS.showDays);

  const showHoursAttr = getAttribute(element, 'showHours');
  const showHours = parseBoolean(showHoursAttr, COUNTDOWN_DEFAULTS.showHours);

  const showMinutesAttr = getAttribute(element, 'showMinutes');
  const showMinutes = parseBoolean(showMinutesAttr, COUNTDOWN_DEFAULTS.showMinutes);

  const showSecondsAttr = getAttribute(element, 'showSeconds');
  const showSeconds = parseBoolean(showSecondsAttr, COUNTDOWN_DEFAULTS.showSeconds);

  // Parse custom labels
  const labelDaysAttr = getAttribute(element, 'labelDays');
  const labelDays = labelDaysAttr || COUNTDOWN_DEFAULTS.labelDays;

  const labelHoursAttr = getAttribute(element, 'labelHours');
  const labelHours = labelHoursAttr || COUNTDOWN_DEFAULTS.labelHours;

  const labelMinutesAttr = getAttribute(element, 'labelMinutes');
  const labelMinutes = labelMinutesAttr || COUNTDOWN_DEFAULTS.labelMinutes;

  const labelSecondsAttr = getAttribute(element, 'labelSeconds');
  const labelSeconds = labelSecondsAttr || COUNTDOWN_DEFAULTS.labelSeconds;

  // Parse number typography
  const numberFontFamilyAttr = getAttribute(element, 'numberFontFamily');
  const numberFontFamily = numberFontFamilyAttr || COUNTDOWN_DEFAULTS.numberFontFamily;

  const numberFontWeightAttr = getAttribute(element, 'numberFontWeight');
  const numberFontWeight = numberFontWeightAttr || COUNTDOWN_DEFAULTS.numberFontWeight;

  const numberFontStyleAttr = getAttribute(element, 'numberFontStyle');
  const numberFontStyle = numberFontStyleAttr || COUNTDOWN_DEFAULTS.numberFontStyle;

  const numberFontSizeAttr = getAttribute(element, 'numberFontSize');
  const numberFontSize = numberFontSizeAttr || COUNTDOWN_DEFAULTS.numberFontSize;

  const numberLetterSpacingAttr = getAttribute(element, 'numberLetterSpacing');
  const numberLetterSpacing = parseFloat_(
    numberLetterSpacingAttr,
    COUNTDOWN_DEFAULTS.numberLetterSpacing,
  );

  const numberColorAttr = getAttribute(element, 'numberColor');
  const numberTextColor = numberColorAttr
    ? parseColor(numberColorAttr)
    : COUNTDOWN_DEFAULTS.numberTextColor;

  // Parse label typography
  const labelFontFamilyAttr = getAttribute(element, 'labelFontFamily');
  const labelFontFamily = labelFontFamilyAttr || COUNTDOWN_DEFAULTS.labelFontFamily;

  const labelFontWeightAttr = getAttribute(element, 'labelFontWeight');
  const labelFontWeight = labelFontWeightAttr || COUNTDOWN_DEFAULTS.labelFontWeight;

  const labelFontStyleAttr = getAttribute(element, 'labelFontStyle');
  const labelFontStyle = labelFontStyleAttr || COUNTDOWN_DEFAULTS.labelFontStyle;

  const labelFontSizeAttr = getAttribute(element, 'labelFontSize');
  const labelFontSize = labelFontSizeAttr || COUNTDOWN_DEFAULTS.labelFontSize;

  const labelLetterSpacingAttr = getAttribute(element, 'labelLetterSpacing');
  const labelLetterSpacing = parseFloat_(
    labelLetterSpacingAttr,
    COUNTDOWN_DEFAULTS.labelLetterSpacing,
  );

  const labelColorAttr = getAttribute(element, 'labelColor');
  const labelTextColor = labelColorAttr
    ? parseColor(labelColorAttr)
    : COUNTDOWN_DEFAULTS.labelTextColor;

  // Parse layout
  const gapAttr = getAttribute(element, 'gap');
  const gap = parseInteger(gapAttr, COUNTDOWN_DEFAULTS.gap);

  const widthAttr = getAttribute(element, 'width');
  const width = widthAttr || COUNTDOWN_DEFAULTS.width;

  const heightAttr = getAttribute(element, 'height');
  const height = heightAttr || COUNTDOWN_DEFAULTS.height;

  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, COUNTDOWN_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, COUNTDOWN_DEFAULTS.margin);

  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, COUNTDOWN_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, COUNTDOWN_DEFAULTS.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : undefined;

  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : undefined;

  // Parse click event
  const clickUrlAttr = getAttribute(element, 'clickUrl');
  const openInNewTabAttr = getAttribute(element, 'openInNewTab');
  const openInNewTab = parseBoolean(openInNewTabAttr, false);
  const clickEvent = buildClickEvent(clickUrlAttr, openInNewTab);

  // Parse expire event
  const expireUrlAttr = getAttribute(element, 'expireUrl');
  const expireInNewTabAttr = getAttribute(element, 'expireInNewTab');
  const expireInNewTab = parseBoolean(expireInNewTabAttr, false);
  const expireEvent = buildExpireEvent(expireUrlAttr, expireInNewTab);

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || COUNTDOWN_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, COUNTDOWN_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides (tablet-*, mobile-*)
  const tabletOverrides = parseBreakpointOverrides(element, 'tablet');
  const mobileOverrides = parseBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    mode,
    dateTime,
    evergreenId,
    evergreenDuration,
    type,
    showDays,
    showHours,
    showMinutes,
    showSeconds,
    labelDays,
    labelHours,
    labelMinutes,
    labelSeconds,
    numberFontFamily,
    numberFontWeight,
    numberFontStyle,
    numberFontSize,
    numberLetterSpacing,
    numberTextColor,
    labelFontFamily,
    labelFontWeight,
    labelFontStyle,
    labelFontSize,
    labelLetterSpacing,
    labelTextColor,
    gap,
    width,
    height,
    padding,
    margin,
    borderRadius,
    borderWidth,
    clickEvent,
    expireEvent,
    // Position props
    position,
    top: top || COUNTDOWN_DEFAULTS.top,
    left: left || COUNTDOWN_DEFAULTS.left,
    right: right || COUNTDOWN_DEFAULTS.right,
    bottom: bottom || COUNTDOWN_DEFAULTS.bottom,
    positionAnchorsState,
    zIndex,
    fillSpace: getLeafFillSpace(context.parentFlexDirection, COUNTDOWN_DEFAULTS.fillSpace),
    flexBasis: COUNTDOWN_DEFAULTS.flexBasis,
    flexGrow: COUNTDOWN_DEFAULTS.flexGrow,
    visibility: COUNTDOWN_DEFAULTS.visibility,
  };

  // Only include optional props if they have values
  if (borderColor) {
    props.borderColor = borderColor;
  }
  if (backgroundColor) {
    props.backgroundColor = backgroundColor;
  }
  if (shadow) {
    props.fieldShadowType = shadow.shadowType;
    props.fieldShadowSettings = shadow.shadowSettings;
  }

  // Add tablet overrides if any
  if (Object.keys(tabletOverrides).length > 0) {
    props.tablet = tabletOverrides;
  }

  // Add mobile overrides, merging with defaults
  props.mobile = {
    ...COUNTDOWN_DEFAULTS.mobile,
    ...mobileOverrides,
  };

  return {
    type: { resolvedName: COUNTDOWN_NODE_NAME },
    isCanvas: false,
    props,
    displayName: COUNTDOWN_NODE_NAME,
    custom: {
      displayName: COUNTDOWN_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Map mode attribute to CraftJS mode value
 */
const mapMode = (value: string | null): 'standard' | 'daily' | 'evergreen' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'standard':
      return 'standard';
    case 'daily':
      return 'daily';
    case 'evergreen':
      return 'evergreen';
    default:
      return undefined;
  }
};

/**
 * Map type attribute to CraftJS type value
 */
const mapType = (value: string | null): 'default' | 'short' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'default':
      return 'default';
    case 'short':
      return 'short';
    default:
      return undefined;
  }
};

/**
 * Get default dateTime (24 hours from now at noon)
 */
const getDefaultDateTime = (mode: 'standard' | 'daily' | 'evergreen'): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(12, 0, 0, 0);

  if (mode === 'daily') {
    // For daily mode, return date without timezone (YYYY-MM-DDTHH:mm:ss format)
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    const hours = String(tomorrow.getHours()).padStart(2, '0');
    const minutes = String(tomorrow.getMinutes()).padStart(2, '0');
    const seconds = String(tomorrow.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  return tomorrow.toISOString();
};

/**
 * Generate a unique evergreen ID
 */
const generateEvergreenId = (): string => {
  return `eg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Build click event from URL and openInNewTab
 */
const buildClickEvent = (url: string | null, openInNewTab: boolean) => {
  if (!url) {
    return {
      eventType: NO_LINK_EVENT,
      eventData: null,
    };
  }

  return {
    eventType: EXTERNAL_LINK_EVENT,
    eventData: {
      url,
      openInNewTab,
    },
  };
};

/**
 * Build expire event from URL and openInNewTab
 */
const buildExpireEvent = (url: string | null, openInNewTab: boolean) => {
  if (!url) {
    return {
      eventType: STAY_ON_PAGE,
      eventData: null,
    };
  }

  return {
    eventType: EXTERNAL_LINK_EVENT,
    eventData: {
      url,
      openInNewTab,
    },
  };
};

/**
 * Parse breakpoint-prefixed attributes (tablet-*, mobile-*)
 */
const parseBreakpointOverrides = (
  element: Element,
  prefix: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};
  const attrs = Array.from(element.attributes);

  for (const attr of attrs) {
    if (!attr.name.startsWith(`${prefix}-`)) continue;

    const propName = attr.name.slice(prefix.length + 1);

    // Only allow specific properties to be overridden
    if (
      !BREAKPOINT_COUNTDOWN_ATTRIBUTES.includes(
        propName as (typeof BREAKPOINT_COUNTDOWN_ATTRIBUTES)[number],
      )
    ) {
      continue;
    }

    // Parse the value based on the property type
    switch (propName) {
      case 'padding':
      case 'margin':
        overrides[propName] = parsePadding(attr.value, [0, 0, 0, 0]);
        break;
      case 'borderRadius':
        overrides[propName] = parseBorderRadius(attr.value, [0, 0, 0, 0]);
        break;
      case 'position': {
        const parsedPosition = parsePosition(attr.value);
        if (parsedPosition) overrides.position = parsedPosition;
        break;
      }
      case 'top':
        overrides.top = parsePositionOffset(attr.value);
        break;
      case 'left':
        overrides.left = parsePositionOffset(attr.value);
        break;
      case 'right':
        overrides.right = parsePositionOffset(attr.value);
        break;
      case 'bottom':
        overrides.bottom = parsePositionOffset(attr.value);
        break;
      case 'zIndex':
        overrides.zIndex = parseInteger(attr.value, 0);
        break;
    }
  }

  // Infer positionAnchorsState if any position props are provided
  if (
    overrides.position !== undefined ||
    overrides.top !== undefined ||
    overrides.left !== undefined ||
    overrides.right !== undefined ||
    overrides.bottom !== undefined
  ) {
    overrides.positionAnchorsState = inferPositionAnchorsState(
      overrides.position as PositionValue | undefined,
      overrides.top as string | undefined,
      overrides.left as string | undefined,
      overrides.right as string | undefined,
      overrides.bottom as string | undefined,
    );
  }

  return overrides;
};

/**
 * Breakpoint-overridable properties for countdown
 */
export const BREAKPOINT_COUNTDOWN_ATTRIBUTES = [
  'padding',
  'margin',
  'borderRadius',
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
] as const;

/**
 * Valid attributes for countdown elements
 */
export const COUNTDOWN_ATTRIBUTES = [
  // Mode & configuration
  'mode',
  'dateTime',
  'evergreenId',
  'evergreenDuration',
  'type',
  // Unit visibility
  'showDays',
  'showHours',
  'showMinutes',
  'showSeconds',
  // Custom labels
  'labelDays',
  'labelHours',
  'labelMinutes',
  'labelSeconds',
  // Layout
  'width',
  'height',
  'padding',
  'margin',
  'gap',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'bg',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Number typography
  'numberFontFamily',
  'numberFontWeight',
  'numberFontStyle',
  'numberFontSize',
  'numberLetterSpacing',
  'numberColor',
  // Label typography
  'labelFontFamily',
  'labelFontWeight',
  'labelFontStyle',
  'labelFontSize',
  'labelLetterSpacing',
  'labelColor',
  // Events
  'clickUrl',
  'openInNewTab',
  'expireUrl',
  'expireInNewTab',
] as const;

/**
 * Check if an attribute is valid for countdown elements
 * Handles both base attributes and breakpoint-prefixed attributes (tablet-*, mobile-*)
 */
export const isValidCountdownAttribute = (attr: string): boolean => {
  // Check base attributes
  if ((COUNTDOWN_ATTRIBUTES as readonly string[]).includes(attr)) {
    return true;
  }

  // Check breakpoint-prefixed attributes (tablet-padding, mobile-margin, etc.)
  let propName: string | null = null;
  if (attr.startsWith('tablet-')) {
    propName = attr.slice(7);
  } else if (attr.startsWith('mobile-')) {
    propName = attr.slice(7);
  }

  if (propName) {
    return (BREAKPOINT_COUNTDOWN_ATTRIBUTES as readonly string[]).includes(propName);
  }

  return false;
};
